import { useAuth } from "@clerk/clerk-react";
import { useAction } from "convex/react";
import { useEffect, useRef } from "react";
import { api } from "../../convex/_generated/api";

/**
 * useSubscriptionSync
 *
 * Triggers a one-time Polar.sh subscription sync for the authenticated user
 * when the component mounts. Ensures subscription entitlements persist across
 * logins without requiring a fresh webhook event.
 *
 * The sync is gated behind auth (only fires when the user is signed in) and
 * is de-duplicated: the backend skips re-syncing if the user was synced within
 * the configured `POLAR_SYNC_INTERVAL_MINUTES` window.
 *
 * Place this hook once in a layout or provider that wraps authenticated routes.
 *
 * @example
 * ```tsx
 * function DashboardLayout({ children }) {
 *   useSubscriptionSync();
 *   return <>{children}</>;
 * }
 * ```
 */
export function useSubscriptionSync(): void {
  const { isSignedIn } = useAuth();
  const syncAction = useAction(api.subscriptionSyncActions.syncMySubscriptionFromPolar);
  const hasFired = useRef(false);

  useEffect(() => {
    if (!isSignedIn || hasFired.current) return;
    hasFired.current = true;

    syncAction({}).catch((err) => {
      // Non-critical — subscription data is still served from Convex cache.
      // The cron poller will eventually reconcile any drift.
      console.warn("Subscription sync failed:", err);
    });
  }, [isSignedIn, syncAction]);
}
